import { useContext, useEffect, useState } from 'react';

import { Alert, Button, IconButton, Snackbar } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useVariant } from '@unleash/proxy-client-react';
import {
  informationInviteAtom,
  infoSessionCancelledAtom,
} from 'atoms/inviteSession';
import { useAtom, useSetAtom } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';
import { FeatureToggle } from 'utils/unleash/toggles';

import { useCareMatrix } from 'hooks/useCareMatrix';
import { ErrorInvite } from 'services/index';
import { createInviteSession } from 'services/specialist/specialist';

import Body from './Components/Body';
import { DEFAULT_MINIMUM_INVITE_TIME, patientObject } from './constants';
import { DrawerInviteScheduleContext } from './context/DrawerInviteScheduleContext';
import { getInviteDate, isMinimumInviteTime } from './utils';

import { DrawerInviteScheduleProps } from './types';

import * as S from './styles';

const DrawerInviteSchedule: React.FC<DrawerInviteScheduleProps> = ({
  onCloseDrawer,
  openDrawerInviteCreated,
}) => {
  const intl = useIntl();
  const { closeModule } = useCareMatrix();
  const minimumInviteTime = parseInt(
    useVariant(FeatureToggle.MinimumInviteTime)?.payload?.value ||
      DEFAULT_MINIMUM_INVITE_TIME,
    10,
  );

  const setInformationInvite = useSetAtom(informationInviteAtom);
  const [infoSessionCancelled, setInfoSessionCancelled] = useAtom(
    infoSessionCancelledAtom,
  );

  const {
    date,
    setDate,
    hour,
    setHour,
    patient,
    setPatient,
    isChecked,
    setIsChecked,
    error,
    step,
    setStep,
    isTermsChecked,
  } = useContext(DrawerInviteScheduleContext);

  const [snackbar, setSnackbar] = useState({
    isOpen: false,
    message: 'ENDUSER.DRAWER.CRITICAL.NOTIFICATION.WARNING',
  });

  const isDisabledButton = Boolean(
    !date ||
      !hour ||
      (!patient.fullName && !infoSessionCancelled?.fullName) ||
      !isTermsChecked,
  );

  const { mutate, isLoading } = useMutation({
    mutationFn: createInviteSession,
    onSuccess: (response: string | ErrorInvite) => {
      if (response === 'PAST_DATE') {
        return setSnackbar({
          isOpen: true,
          message: intl.formatMessage({
            id: 'CPPLATAFORM.CPNEWTIME.ERRORBANNER',
          }),
        });
      }

      if (response === 'LIMIT_INVITE_DATE') {
        return setSnackbar({
          isOpen: true,
          message: intl.formatMessage(
            {
              id: 'CPPLATAFORM.CPNEWTIME.ERRORBANNERSCHEDULE',
            },
            { time: minimumInviteTime },
          ),
        });
      }

      setInformationInvite({
        inviteLink: response,
        patientName: infoSessionCancelled?.fullName ?? patient.fullName,
        sessionCreated: date?.toString() || '',
      });

      if (infoSessionCancelled) {
        closeModule();
      }

      return openDrawerInviteCreated();
    },
    onError: () => {
      setSnackbar({
        isOpen: true,
        message: 'ENDUSER.DRAWER.CRITICAL.NOTIFICATION.WARNING',
      });
    },
  });

  useEffect(() => {
    if (infoSessionCancelled?.fullName && step === 1) {
      setIsChecked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, infoSessionCancelled]);

  const disabledProps = () => {
    if (infoSessionCancelled?.fullName && step !== 1) {
      return { disabled: !isChecked };
    }

    return {
      disabled:
        isDisabledButton ||
        isLoading ||
        error?.isDateError ||
        error?.isTimeError,
    };
  };

  const handleRescheduleSession = () => {
    if (!infoSessionCancelled || !date || !hour) {
      return null;
    }

    const dateFormat = getInviteDate({ date, hour });

    return mutate({
      userId: infoSessionCancelled.enduserID,
      date: dateFormat,
      sessionId: infoSessionCancelled.sessionID,
    });
  };

  const handleConfirmButton = () => {
    if (step === 1 && hour && date) {
      const dateFormat = getInviteDate({ date, hour });

      if (isMinimumInviteTime(dateFormat, minimumInviteTime)) {
        return setSnackbar({
          isOpen: true,
          message: intl.formatMessage(
            {
              id: 'CPPLATAFORM.CPNEWTIME.ERRORBANNERSCHEDULE',
            },
            { time: minimumInviteTime },
          ),
        });
      }
      return setStep(2);
    }

    if (date && hour && infoSessionCancelled) {
      return handleRescheduleSession();
    }

    if (date && hour) {
      const dateFormat = getInviteDate({ date, hour });

      return mutate({
        userId: patient.userId,
        date: dateFormat,
      });
    }

    return null;
  };

  const handleCloseDrawer = () => {
    setDate(null);
    setHour(null);
    setPatient(patientObject);
    setInfoSessionCancelled(null);
    onCloseDrawer();
  };

  const handleBackButton = () => {
    if (step === 2) {
      return setStep(1);
    }

    return handleCloseDrawer();
  };

  return (
    <>
      <S.Container>
        <S.Header>
          <IconButton onClick={handleBackButton}>
            <S.ArrowLeftIcon />
          </IconButton>
          <S.Title variant="subtitle2">
            <FormattedMessage id="CPPLATFORM.CPINVITECREATING.HEADER" />
          </S.Title>
          <IconButton onClick={handleCloseDrawer}>
            <S.CloseIcon />
          </IconButton>
        </S.Header>

        <Body />

        <S.Footer>
          <Button
            size="large"
            variant="contained"
            onClick={handleConfirmButton}
            {...disabledProps()}
          >
            <FormattedMessage id="CPPLATFORM.CPINVITECREATING.CONFIRMBUTTON" />
          </Button>
          <Button
            size="large"
            onClick={step === 1 ? handleCloseDrawer : handleBackButton}
          >
            <FormattedMessage
              id={
                step === 1
                  ? 'CPPLATFORM.CPINVITECREATING.CANCELBUTTON'
                  : 'CPPLATFORM.CPINVITECONFIRM.BACKBUTTON'
              }
            />
          </Button>
        </S.Footer>
      </S.Container>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={snackbar.isOpen}
        autoHideDuration={3000}
        onClose={() => {
          setSnackbar({
            isOpen: false,
            message: snackbar.message,
          });
        }}
      >
        <Alert
          onClose={() =>
            setSnackbar({
              isOpen: false,
              message: snackbar.message,
            })
          }
          icon={<S.CustomWarningAmberIcon />}
          severity="warning"
          sx={{ width: '100%' }}
        >
          <FormattedMessage id={snackbar.message} />
        </Alert>
      </Snackbar>
    </>
  );
};

export default DrawerInviteSchedule;
